import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, Heading, Text } from '../ui/index.js';
import type { Access, PublicUser } from '@goodbuild/shared';
import { api } from '../api/client.js';
import { keys, useAccess } from '../api/hooks.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { FocusableTextField } from '../components/focus/OnScreenKeyboard.js';
import { EmptyState, Loading } from '../components/layout/Loading.js';
import { formatDate, formatRelative } from '../lib/format.js';

export const Route = createFileRoute('/admin/members')({ component: AdminMembers });

interface SearchResult {
  users: Array<{ user: PublicUser; access: Access }>;
}

interface MemberDetail {
  user: PublicUser;
  createdAt: number;
  access: Access;
  memberships: Array<{
    id: string;
    plan_key: string;
    valid: number;
    status: string;
    current_period_end: number | null;
    cancel_at_period_end: number;
  }>;
  crew: { id: string; owner_user_id: string } | null;
  sessions: Array<{
    id: string;
    platform: string;
    last_used_at: number;
    revoked_at: number | null;
  }>;
  audit: Array<{ id: string; action: string; created_at: number }>;
}

/** Member lookup, so support questions can be answered without a database console. */
function AdminMembers() {
  const { isAdmin } = useAccess();
  const [query, setQuery] = React.useState('');
  const [submitted, setSubmitted] = React.useState('');
  const [openId, setOpenId] = React.useState<string | null>(null);

  const results = useQuery({
    queryKey: keys.admin.users(submitted),
    queryFn: () => api.get<SearchResult>('/admin/users', { q: submitted }),
    enabled: isAdmin && submitted.length > 0,
  });

  const detail = useQuery({
    queryKey: ['admin', 'users', 'detail', openId],
    queryFn: () => api.get<MemberDetail>(`/admin/users/${openId}`),
    enabled: Boolean(openId),
  });

  if (!isAdmin) return <EmptyState title={'You cannot do that.'} />;

  return (
    <div className="gb-stack">
      <Card size="3">
        <FocusableTextField
          value={query}
          onChange={setQuery}
          label={'Search by username'}
          maxLength={64}
          autoFocus
        />
        <div style={{ marginTop: '0.5rem' }}>
          <Focusable onSelect={() => setSubmitted(query.trim())} selectLabel={'Search'}>
            <Button size="2">{'Search'}</Button>
          </Focusable>
        </div>
      </Card>

      {results.isLoading ? <Loading /> : null}

      <FocusZone focusKey="admin-members" direction="column" className="gb-stack">
        {(results.data?.users ?? []).map((row) => (
          <Card key={row.user.id} size="2">
            <div className="gb-seat">
              <Text size="2">{row.user.displayName ?? row.user.username}</Text>
              <Badge color={row.access.hasAccess ? 'green' : 'gray'}>
                {row.access.source ?? 'None'}
              </Badge>
              {row.access.planKey ? <Badge>{row.access.planKey}</Badge> : null}
              <span className="gb-seat__spacer" />
              {row.access.accessUntil ? (
                <Text size="1" color="gray">
                  {formatDate(row.access.accessUntil)}
                </Text>
              ) : null}
              <Focusable
                onSelect={() => setOpenId(openId === row.user.id ? null : row.user.id)}
                selectLabel={'Open'}
              >
                <Button size="1" variant="surface">
                  {'Open'}
                </Button>
              </Focusable>
            </div>

            {openId === row.user.id && detail.data ? (
              <div className="gb-stack" style={{ marginTop: '0.75rem' }}>
                <Heading size="3">{'Plan'}</Heading>
                {detail.data.memberships.map((m) => (
                  <Text key={m.id} size="2" color="gray">
                    {m.plan_key} · {m.status} · {m.valid ? 'Yes' : 'No'}
                    {m.current_period_end ? ` · ${formatDate(m.current_period_end)}` : ''}
                  </Text>
                ))}

                <Heading size="3">{'Settings'}</Heading>
                {detail.data.sessions.map((s) => (
                  <Text key={s.id} size="1" color="gray">
                    {s.platform} · {formatRelative(s.last_used_at)}
                    {s.revoked_at ? ' · revoked' : ''}
                  </Text>
                ))}

                <Heading size="3">{'Audit log'}</Heading>
                {detail.data.audit.slice(0, 10).map((a) => (
                  <Text key={a.id} size="1" color="gray">
                    {a.action} · {formatRelative(a.created_at)}
                  </Text>
                ))}
              </div>
            ) : null}
          </Card>
        ))}
      </FocusZone>
    </div>
  );
}
